import React, { useState, useEffect, useCallback } from 'react';
import { ChatbotPersonality } from '../types/chatbot';
import { useCompanions } from '../hooks/useCompanions';
import { NormalizedCompanion } from '../adapters/companionAdapter';
import CreateCompanionModal from './CreateCompanionModal';
import CompanionDetailModal from './CompanionDetailModal';
import OptimizedImage from './ui/OptimizedImage';
import OptimizedVideo from './ui/OptimizedVideo';

interface ChatbotStats {
  likeCount: number;
  chatCount: number;
  isLiked: boolean;
  hasStartedChat: boolean;
}

interface ChatbotSelectorProps {
  onSelect: (chatbot: ChatbotPersonality) => void;
  selectedChatbot?: ChatbotPersonality;
  chatbotStats: { [key: string]: ChatbotStats };
  category?: string;
}



// This conversion is now handled by the CompanionAdapter
// Keeping this for any legacy compatibility needs
const convertCompanionToChatbotPersonality = (companion: NormalizedCompanion): ChatbotPersonality => {
  return {
    id: companion.id,
    name: companion.displayName,
    age: companion.age,
    personality: companion.positiveTraits.join(', ') || '',
    description: companion.description,
    welcomeMessage: `Hello! I'm ${companion.displayName}. Nice to meet you!`,
    systemPrompt: `You are ${companion.displayName}, ${companion.description || 'a friendly AI companion'}.`,
    avatar: companion.primaryPhoto || companion.primaryVideo || '',
    avatarType: companion.primaryVideo ? 'video' : 'image',
    posterUrl: companion.posterUrl || '',
    gallery: companion.galleryAssets.filter(asset => asset.type === 'image').map(asset => asset.url),
    galleryAssets: companion.galleryAssets,
    mood: 'caring' as any,
    specialties: companion.categories,
    attributes: {
      physical: {
        ethnicity: companion.physicalAttributes.ethnicity,
        eye_color: companion.physicalAttributes.eyeColor,
        hair_style: companion.physicalAttributes.hairStyle,
        hair_color: companion.physicalAttributes.hairColor,
        body_type: companion.physicalAttributes.bodyType,
      },
      personal: {
        occupation: companion.personalAttributes.occupation,
        hobby: companion.personalAttributes.hobbies.join(', ') || undefined,
      }
    }
  };
};

// Component for individual companion cards with hover video functionality
const CompanionHoverCard: React.FC<{
  chatbot: ChatbotPersonality;
  stats: ChatbotStats;
  onSelect: (chatbot: ChatbotPersonality) => void;
  selectedChatbot?: ChatbotPersonality;
  allCompanions: NormalizedCompanion[];
  currentlyPlayingVideo: string | null;
  setCurrentlyPlayingVideo: (videoUrl: string | null) => void;
}> = React.memo(({ chatbot, stats, onSelect, selectedChatbot, allCompanions, currentlyPlayingVideo, setCurrentlyPlayingVideo }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [videoActivated, setVideoActivated] = useState(false);

  // Find the companion data to get primary_photo and primary_video
  const companion = React.useMemo(() =>
    allCompanions.find(c => c.id === chatbot.id),
    [allCompanions, chatbot.id]
  );
  const hasVideo = companion?.primaryVideo;
  const hasPhoto = companion?.primaryPhoto;

  // Determine what to show on hover/touch vs normal state
  // Show video if: activated by hover/touch OR currently playing (mobile)
  const videoUrl = companion?.primaryVideo;
  const shouldShowVideo = hasVideo && (videoActivated || currentlyPlayingVideo === videoUrl);
  const shouldAutoPlay = isHovered || currentlyPlayingVideo === videoUrl;

  // No longer need video activation logic - using global state

  // Activate video when first hovered or touched
  React.useEffect(() => {
    if ((isHovered || currentlyPlayingVideo === videoUrl) && hasVideo && !videoActivated) {
      setVideoActivated(true);
    }
  }, [isHovered, currentlyPlayingVideo, videoUrl, hasVideo, videoActivated]);

  // Handle touch events for mobile
  const handleTouchStart = () => {
    if (hasVideo && videoUrl) {
      // Set this video as the currently playing one (stops others)
      setCurrentlyPlayingVideo(videoUrl);
    }
  };

  const handleTouchEnd = () => {
    // No longer need to stop video after timeout - it plays until another is touched
  };

  // Debug logging
  useEffect(() => {
    console.log('CompanionHoverCard state:', {
      name: chatbot.name,
      isHovered,
      hasVideo,
      shouldShowVideo,
      videoUrl: companion?.primaryVideo,
      currentlyPlaying: currentlyPlayingVideo === videoUrl
    });
  }, [isHovered, hasVideo, shouldShowVideo, chatbot.name, companion?.primaryVideo, currentlyPlayingVideo, videoUrl]);

  return (
    <div
      onClick={() => onSelect(chatbot)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Strict Container - Nothing can render outside this */}
      <div className="relative group cursor-pointer transform hover:scale-105 transition-all duration-300 rounded-2xl overflow-hidden bg-transparent shadow-lg hover:shadow-2xl" style={{
        aspectRatio: '3/4',
        borderRadius: '1rem',
        contain: 'layout style paint',
        backgroundColor: 'transparent',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
        border: 'none',
        outline: 'none'
      }}>
        {/* Inner Content Container */}
        <div className="absolute inset-0 w-full h-full rounded-2xl overflow-hidden" style={{
          backgroundColor: 'transparent',
          border: 'none',
          outline: 'none',
          margin: '0',
          padding: '0'
        }}>
          {/* Always render image as base layer */}
          {chatbot.avatar && (
            <OptimizedImage
              src={chatbot.avatar}
              alt={chatbot.name}
              className={`w-full h-full object-top absolute inset-0 transition-opacity duration-300 ${shouldShowVideo ? 'opacity-0' : 'opacity-100'
                }`}
              placeholder="👤"
              priority={false}
            />
          )}

          {/* Always render video when available, control visibility */}
          {hasVideo && (
            <div
              className={`w-full h-full absolute inset-0 transition-opacity duration-300 ${shouldShowVideo ? 'opacity-100' : 'opacity-0'
                }`}
              style={{
                zIndex: shouldShowVideo ? 2 : 1,
                pointerEvents: shouldShowVideo ? 'auto' : 'none'
              }}
            >
              <OptimizedVideo
                src={companion?.primaryVideo || ''}
                poster={hasPhoto ? companion?.primaryPhoto || undefined : chatbot.posterUrl}
                className="w-full h-full"
                autoPlay={!!shouldAutoPlay}
                loop={true}
                muted={true}
                controls={false}
                priority={true}
                pauseOnHoverEnd={false}
                style={{
                  borderRadius: '1rem',
                  WebkitBorderRadius: '1rem'
                }}
              />
            </div>
          )}

          {/* Fallback for no avatar */}
          {!chatbot.avatar && !hasVideo && (
            <div className="w-full h-full bg-gradient-to-br from-accent-purple to-accent-pink flex items-center justify-center">
              <span className="text-6xl">🤖</span>
            </div>
          )}

          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" style={{ zIndex: 2 }}></div>


          {/* Bottom content */}
          <div className="absolute bottom-0 left-0 right-0 p-4" style={{ zIndex: 3 }}>
            {/* Name and Age */}
            <div className="mb-2">
              <h3 className="text-white text-2xl font-bold mb-1">{chatbot.name}</h3>
              <p className="text-white/80 text-sm font-medium">Age: {chatbot.age}</p>
            </div>

            {/* Description */}
            <div className="mb-3">
              <p className="text-white/90 text-sm leading-relaxed line-clamp-2 overflow-hidden">
                {chatbot.description || `Meet ${chatbot.name}, your AI companion.`}
              </p>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-white/70">
              <span className="flex items-center gap-1.5">
                <span className="text-red-400">❤️</span>
                <span className="text-sm font-medium">{stats.likeCount}</span>
              </span>
              <span className="flex items-center gap-1.5">
                <span className="text-blue-400">💬</span>
                <span className="text-sm font-medium">{stats.chatCount}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

const ChatbotSelector: React.FC<ChatbotSelectorProps> = ({ onSelect, selectedChatbot, chatbotStats, category }) => {
  // Use the new centralized hook
  const { 
    companions: allCompanions, 
    loading, 
    error,
    filterByCategory 
  } = useCompanions();

  const [currentlyPlayingVideo, setCurrentlyPlayingVideo] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCompanionDetail, setSelectedCompanionDetail] = useState<NormalizedCompanion | null>(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [bannerImages, setBannerImages] = useState<string[]>([]);
  const [displayCount, setDisplayCount] = useState(12); // Initial number of companions to show

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return !!token;
  }, []);

  // Load banner images
  const loadBannerImages = useCallback(() => {
    // Use the create_now.webp file
    const bannerPath = '/images/banner/create_now.webp';
    setBannerImages([bannerPath]);
  }, []);

  useEffect(() => {
    loadBannerImages();
  }, [loadBannerImages]);

  // Reset display count when category changes
  useEffect(() => {
    setDisplayCount(12);
  }, [category]);

  // Auto-rotate banner images
  useEffect(() => {
    if (bannerImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prev) => (prev + 1) % bannerImages.length);
      }, 5000); // Change every 5 seconds

      return () => clearInterval(interval);
    }
  }, [bannerImages.length]);

  const nextBanner = () => {
    setCurrentBannerIndex((prev) => (prev + 1) % bannerImages.length);
  };

  const prevBanner = () => {
    setCurrentBannerIndex((prev) => (prev - 1 + bannerImages.length) % bannerImages.length);
  };

  const handleCompanionCreated = useCallback(() => {
    // The hook will automatically refresh the data
    window.location.reload(); // Simple refresh for now
  }, []);

  // Skeleton loading component
  const SkeletonCard = () => (
    <div className="relative group animate-pulse">
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-dark-800">
        {/* Image placeholder */}
        <div className="w-full h-full bg-gradient-to-br from-dark-700 to-dark-800 flex items-center justify-center">
          <svg className="w-16 h-16 text-dark-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17l2.5-3.16L14.5 17H9z" />
          </svg>
        </div>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

        {/* Top right skeleton buttons */}
        <div className="absolute top-3 right-3 flex gap-2">
          <div className="w-8 h-8 bg-dark-600 rounded-full"></div>
          <div className="w-8 h-8 bg-dark-600 rounded-full"></div>
        </div>

        {/* Bottom content skeleton */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Name skeleton */}
          <div className="h-6 bg-dark-600 rounded mb-2 w-3/4"></div>

          {/* Tags skeleton */}
          <div className="flex gap-1 mb-3">
            <div className="h-5 bg-dark-600 rounded-full w-16"></div>
            <div className="h-5 bg-dark-600 rounded-full w-20"></div>
          </div>

          {/* Description skeleton */}
          <div className="space-y-1 mb-3">
            <div className="h-3 bg-dark-600 rounded w-full"></div>
            <div className="h-3 bg-dark-600 rounded w-2/3"></div>
          </div>

          {/* Stats skeleton */}
          <div className="flex items-center justify-between">
            <div className="flex gap-3">
              <div className="h-4 bg-dark-600 rounded w-8"></div>
              <div className="h-4 bg-dark-600 rounded w-8"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="glass-effect rounded-2xl p-8 border border-dark-600 text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h3 className="text-xl font-semibold text-white mb-2">Connection Error</h3>
        <p className="text-red-400 mb-6">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Promotional Banner */}
      <div
        className="relative rounded-3xl overflow-hidden shadow-2xl min-h-[200px] group cursor-pointer transform hover:scale-[1.01] transition-all duration-300"
        onClick={() => {
          if (isAuthenticated()) {
            window.location.href = '/create';
          } else {
            alert('Please sign in to create AI companions');
          }
        }}
      >
        {/* Background - Image or Gradient */}
        {bannerImages.length > 0 ? (
          <div className="absolute inset-0">
            <img
              src={bannerImages[currentBannerIndex]}
              alt="AI Companion Banner"
              className="w-full h-full object-cover"
              loading="eager"
            />
            {/* Hover overlay to indicate it's clickable for create_ai.webp */}
            {bannerImages[currentBannerIndex] && bannerImages[currentBannerIndex].includes('create_ai.webp') && (
              <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            )}

          </div>
        ) : (
          <>
            {/* Fallback Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-700"></div>
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-black/40"></div>
          </>
        )}

        {/* Navigation Arrows */}
        {bannerImages.length > 1 && (
          <>
            <button
              onClick={prevBanner}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 opacity-0 group-hover:opacity-100 z-20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextBanner}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-black/30 hover:bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all duration-300 opacity-0 group-hover:opacity-100 z-20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {bannerImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2 z-20">
            {bannerImages.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBannerIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentBannerIndex
                  ? 'bg-white'
                  : 'bg-white/50 hover:bg-white/75'
                  }`}
              />
            ))}
          </div>
        )}


      </div>

      {/* Main Header Section */}
      <div className="text-center mb-8">
        <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
          Find Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">Perfect Companion</span>
        </h1>
        <p className="text-lg text-pink-400 mb-8">
          {isAuthenticated()
            ? 'Choose your perfect companion or create your own'
            : 'Discover amazing AI companions - sign in to create your own'
          }
        </p>
      </div>

      <div className="text-center mb-8">
        <div className="mb-6">
          <h1 className="text-4xl font-display font-bold text-white mb-1">
            {category ? `${category.charAt(0).toUpperCase() + category.slice(1)} Companions` : 'Choose Your AI Companion'}
          </h1>
          <div className="text-xl font-display font-semibold gradient-text">
            {category ? `Discover ${category} characters` : 'Start Your Journey'}
          </div>


        </div>

        <p className="text-xl text-dark-300 max-w-2xl mx-auto leading-relaxed mb-6">
          Select a companion to begin meaningful conversations and discover new connections
        </p>

        {isAuthenticated() && (
          <a
            href="/create"
            className="px-8 py-4 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300 text-lg flex items-center gap-2 mx-auto"
          >
            <span>✨</span>
            Create New Companion
          </a>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {loading ? (
          // Show skeleton cards while loading
          Array.from({ length: 8 }).map((_, index) => (
            <SkeletonCard key={`skeleton-${index}`} />
          ))
        ) : (
          (() => {
            // Use the centralized filtering from the hook
            const filtered = category ? filterByCategory(category) : allCompanions;
            
            // Convert to chatbots for display
            const filteredChatbots = filtered.map(companion => 
              convertCompanionToChatbotPersonality(companion)
            );

            // Apply pagination for better performance
            const displayedCompanions = filteredChatbots.slice(0, displayCount);
            const hasMore = filteredChatbots.length > displayCount;

            const companionCards = displayedCompanions.map((chatbot) => {
              const stats = chatbotStats[chatbot.id] || {
                likeCount: 0,
                chatCount: 0,
                isLiked: false,
                hasStartedChat: false,
              };

              return (
                <CompanionHoverCard
                  key={chatbot.id}
                  chatbot={chatbot}
                  stats={stats}
                  onSelect={onSelect}
                  selectedChatbot={selectedChatbot}
                  allCompanions={allCompanions}
                  currentlyPlayingVideo={currentlyPlayingVideo}
                  setCurrentlyPlayingVideo={setCurrentlyPlayingVideo}
                />
              );
            });

            // Add load more button if there are more companions
            if (hasMore) {
              companionCards.push(
                <div key="load-more" className="col-span-full flex justify-center mt-4">
                  <button
                    onClick={() => setDisplayCount(prev => prev + 12)}
                    className="px-6 py-3 bg-gradient-to-r from-accent-purple to-accent-pink text-white rounded-xl font-medium hover:shadow-lg hover:shadow-accent-purple/25 transform hover:scale-[1.02] transition-all duration-300"
                  >
                    Load More ({filteredChatbots.length - displayCount} remaining)
                  </button>
                </div>
              );
            }

            return companionCards;
          })()
        )}
      </div>

      {showCreateModal && isAuthenticated() && (
        <CreateCompanionModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onCompanionCreated={handleCompanionCreated}
        />
      )}

      {selectedCompanionDetail && (
        <CompanionDetailModal
          companion={selectedCompanionDetail as any} // Type compatibility for now
          isOpen={!!selectedCompanionDetail}
          onClose={() => setSelectedCompanionDetail(null)}
          onStartChat={() => {
            const chatbot = convertCompanionToChatbotPersonality(selectedCompanionDetail);
            onSelect(chatbot);
            setSelectedCompanionDetail(null);
          }}
        />
      )}
    </div>
  );
};

export default ChatbotSelector;